import React, { useEffect, useRef, useState } from 'react';
import { Target, Tag, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmationDialog,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioButtonGroup,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@roger/ui-components';
import type {
  GoalProgressDetail,
  GoalProgressScore,
  InterventionTemplateDetail,
  NextEpisodeResponse,
  PatientGoalDetail,
  PatientInterventionDetail,
} from '../../services/api/types.gen';
import { GOALPROGRESSSCORE_VALUES } from '../../services/api/models/GoalProgressScore';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  acceptImportedGoal,
  getInterventionsForPatientGoal,
  getPatientGoalsForVisit,
  recordGoalProgress,
  deleteGoalFromVisit,
  markPatientGoalMet,
  markPatientGoalUnattainable,
  updateGoalForVisit,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import DisplayInterventionCard from './DisplayInterventionCard';
import CreateInterventionFromTemplateCard from './CreateInterventionFromTemplateCard';
import AddInterventionsBox from './AddInterventionsBox';
import PushGoalDialog from './PushGoalDialog';
import {
  formatDateForDisplay,
  formatDateToISO,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { GOAL_TYPE_OPTIONS } from '../../utils/goals';

// EMR-1586: the row's import caveats, shown inside the Imported-chip popover
// so they are visible exactly while the chip is. Interventions carry their
// own findings but no chip, so they list here under the goal's. The
// intervention list is the card's refreshed saved state, so a deleted
// intervention's caveat leaves with the row.
const ImportFindingsList: React.FC<{
  goal: PatientGoalDetail;
  interventions: PatientInterventionDetail[];
}> = ({ goal, interventions }) => {
  const rows = [
    ...(goal.import_findings ?? []).map(finding => ({
      label: null as string | null,
      finding,
    })),
    ...interventions.flatMap(intervention =>
      (intervention.import_findings ?? []).map(finding => ({
        label: intervention.name || 'Intervention',
        finding,
      }))
    ),
  ];
  if (rows.length === 0) return null;
  return (
    <div className='space-y-1'>
      <p className='text-xs font-semibold'>Import caveats</p>
      <ul className='list-disc space-y-1 pl-4 text-xs'>
        {rows.map((row, index) => (
          <li key={index} className='[overflow-wrap:anywhere]'>
            {row.label ? `${row.label}: ` : ''}
            {row.finding.text}
            {row.finding.original && (
              <span className='mt-0.5 block whitespace-pre-wrap text-muted-foreground'>
                Kinnser&apos;s text: {row.finding.original}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

interface PerformanceBoxProps {
  visitId: string;
  patientGoalId: string;
  onCancel: () => void;
  onSaveSuccess: () => void;
}

const PerformanceBox: React.FC<PerformanceBoxProps> = ({
  visitId,
  patientGoalId,
  onCancel,
  onSaveSuccess,
}) => {
  const { error: showError } = useToast();
  const [progressScore, setProgressScore] = useState<GoalProgressScore | ''>(
    ''
  );
  const [progressNote, setProgressNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    if (!progressScore) return;
    setIsSubmitting(true);
    recordGoalProgress({
      path: { visit_id: visitId, patient_goal_id: patientGoalId },
      body: {
        progress_score: progressScore,
        progress_note: progressNote || undefined,
      },
      throwOnError: true,
    })
      .then(() => {
        onSaveSuccess();
      })
      .catch(() => {
        showError('Failed to record goal progress');
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className='relative space-y-2 rounded border border-border bg-muted/30 p-2 pr-10 text-xs'>
      <button
        type='button'
        onClick={onCancel}
        className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-sm text-lg text-muted-foreground hover:bg-muted hover:text-foreground'
        aria-label='Close'
      >
        ×
      </button>
      <div>
        <Label
          htmlFor={`performance-${patientGoalId}`}
          className='text-muted-foreground'
        >
          Performance
        </Label>
        <Select
          value={progressScore}
          onValueChange={v => setProgressScore(v as GoalProgressScore)}
        >
          <SelectTrigger
            id={`performance-${patientGoalId}`}
            selectSize='sm'
            className='mt-1'
          >
            <SelectValue placeholder='Select performance' />
          </SelectTrigger>
          <SelectContent>
            {GOALPROGRESSSCORE_VALUES.map(val => (
              <SelectItem key={val} value={val}>
                {val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label
          htmlFor={`note-${patientGoalId}`}
          className='text-muted-foreground'
        >
          Progress Note (Optional)
        </Label>
        <Textarea
          id={`note-${patientGoalId}`}
          value={progressNote}
          onChange={e => setProgressNote(e.target.value)}
          placeholder='Add notes...'
          textareaSize='sm'
          className='mt-1 min-h-[60px]'
        />
      </div>
      <div className='flex justify-end'>
        <Button
          size='sm'
          onClick={handleSubmit}
          disabled={!progressScore || isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

interface GoalPerformanceHistoryTableProps {
  progressHistory: GoalProgressDetail[];
}

const GoalPerformanceHistoryTable: React.FC<
  GoalPerformanceHistoryTableProps
> = ({ progressHistory }) => {
  const sortedProgress = [...progressHistory].sort(
    (a, b) =>
      (parseISOToLocalDate(b.task_date)?.getTime() ?? 0) -
      (parseISOToLocalDate(a.task_date)?.getTime() ?? 0)
  );

  if (sortedProgress.length === 0) return null;

  return (
    <Table className='min-w-[560px]'>
      <TableHeader>
        <TableRow>
          <TableHead className='h-8 px-2 py-1 text-[11px] font-medium'>
            Date
          </TableHead>
          <TableHead className='h-8 px-2 py-1 text-[11px] font-medium'>
            Performed by
          </TableHead>
          <TableHead className='h-8 px-2 py-1 text-[11px] font-medium'>
            Progress
          </TableHead>
          <TableHead className='h-8 px-2 py-1 text-[11px] font-medium'>
            Note
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedProgress.map(p => (
          <TableRow key={p.id}>
            <TableCell className='px-2 py-1.5 text-xs align-top'>
              {formatDateForDisplay(p.task_date)}
            </TableCell>
            <TableCell className='px-2 py-1.5 text-xs text-muted-foreground align-top'>
              {/* An imported row was recorded in Kinnser, not in Roger. The
                  clinician is the same human when the account mapped, but the
                  where must not be silently misstated. */}
              {p.imported_at
                ? p.performed_by_email
                  ? `${p.performed_by_email} · in Kinnser`
                  : 'Recorded in Kinnser'
                : (p.performed_by_email ?? '—')}
            </TableCell>
            <TableCell className='px-2 py-1.5 text-xs align-top'>
              {p.progress_score ?? '—'}
            </TableCell>
            <TableCell className='px-2 py-1.5 text-xs text-muted-foreground align-top'>
              {p.progress_note ?? ''}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

function getMostRecentProgressWithScore(
  progressHistory: GoalProgressDetail[] | undefined
): GoalProgressDetail | null {
  if (!progressHistory?.length) return null;
  const withScore = progressHistory.filter(p => p.progress_score != null);
  if (withScore.length === 0) return null;
  const sorted = [...withScore].sort(
    (a, b) =>
      (parseISOToLocalDate(b.task_date)?.getTime() ?? 0) -
      (parseISOToLocalDate(a.task_date)?.getTime() ?? 0)
  );
  return sorted[0] ?? null;
}

interface UnattainableReasonDialogProps {
  open: boolean;
  onAccept: (reason: string) => void;
  onCancel: () => void;
}

const UnattainableReasonDialog: React.FC<UnattainableReasonDialogProps> = ({
  open,
  onAccept,
  onCancel,
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Mark goal as unattainable?</DialogTitle>
          <DialogDescription>
            This action cannot be undone and will mark all interventions for
            this patient as complete.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          placeholder='Enter reason for marking as unattainable...'
          value={reason}
          onChange={e => setReason(e.target.value)}
          textareaSize='default'
          className='min-h-[80px]'
        />
        <DialogFooter>
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onAccept(reason.trim())}
            disabled={!reason.trim()}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface DisplayGoalCardProps {
  goal: PatientGoalDetail;
  visitId: string;
  isViewOnly: boolean;
  /**
   * Whether this visit type can record goal progress. Separate from
   * `isViewOnly`, which tracks visit status: a visit can be perfectly editable
   * and still be the wrong kind of note to record progress on. Required rather
   * than defaulted so a new caller has to state the capability instead of
   * silently opening progress writes the backend will reject.
   */
  canTrackProgress: boolean;
  /**
   * Whether this visit type can attach new interventions. Interventions ride
   * the goal-creation capability (`canAddGoals`): progress-only notes score the
   * existing interventions but cannot add new ones, and the backend rejects the
   * write. Required for the same reason as `canTrackProgress`.
   */
  canAddInterventions: boolean;
  /**
   * Whether this task can mark the goal met or unattainable. True for every
   * progress-eligible visit; an order can never do this (`RECORD_PROGRESS`
   * isn't in its allowed-action set), even though it can still push the goal.
   * Required so a new caller states the capability instead of inheriting
   * `canTrackProgress`'s visit-only meaning.
   */
  canMarkGoalOutcome: boolean;
  /** Whether this task can edit (PUT) the goal. Always true for a visit; always false for an order. */
  canEditGoal: boolean;
  /** Whether this task can delete the goal. Always true for a visit; always false for an order. */
  canDeleteGoal: boolean;
  /**
   * Whether this task can push the goal to the next episode. Separate from
   * `canTrackProgress`/`canMarkGoalOutcome`: an order can push a goal even
   * though it can never record progress or mark it met/unattainable.
   */
  canPushGoal: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canPerformInterventions: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canCompleteInterventions: boolean;
  /** Passed through to each intervention card; see DisplayInterventionCard. */
  canDeleteInterventions: boolean;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /**
   * The episode immediately following the visit's episode, or null when it
   * hasn't been created yet. Fetched once per episode by `GoalsTab` (not per
   * goal card) and passed down so the push-to-next-episode action can be
   * disabled with an explanatory hint instead of offered as a dead button.
   * `undefined` while the parent's fetch is still in flight.
   */
  nextEpisode?: NextEpisodeResponse | null;
  onGoalProgressSaved?: (updatedGoals: PatientGoalDetail[]) => void;
}

const DisplayGoalCard: React.FC<DisplayGoalCardProps> = ({
  goal,
  visitId,
  isViewOnly,
  canTrackProgress,
  canAddInterventions,
  canMarkGoalOutcome,
  canEditGoal,
  canDeleteGoal,
  canPushGoal,
  canPerformInterventions,
  canCompleteInterventions,
  canDeleteInterventions,
  episodeStartDate,
  episodeEndDate,
  nextEpisode,
  onGoalProgressSaved,
}) => {
  const isComplete = !!(goal.is_met || goal.is_unattainable);
  // Extended goals have been pushed to the patient's next episode. Like
  // met/unattainable goals, they're frozen against edit/delete/mark-met/
  // mark-unattainable/add-interventions (backend 400s these, EMR-1378), but
  // unlike those, progress tracking on the goal where it was documented
  // stays allowed (EMR-1380).
  const isExtended = !!goal.is_extended;
  // Push has its own capability (`canPushGoal`) separate from
  // `canTrackProgress`: an order can push a goal even though it can never
  // record progress. It never applies to a goal that is already met,
  // unattainable, or already pushed to the next episode.
  const canShowPushAction =
    !isViewOnly && !isComplete && !goal.is_extended && canPushGoal;
  // The Track Progress dropdown covers three different writes (record
  // progress, mark met, mark unattainable); it should render if any of them
  // is available so a task that can only do one of the three (there is none
  // today, but the props are independent) still gets an entry point.
  const canShowTrackProgressMenu =
    !isViewOnly && !isComplete && (canTrackProgress || canMarkGoalOutcome);
  // Edit/remove: ungated for a visit, always off for an order.
  const canShowEditMenu =
    !isViewOnly && !isComplete && !isExtended && (canEditGoal || canDeleteGoal);
  const { error: showError } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModifyFormOpen, setIsModifyFormOpen] = useState(false);
  const [modifyTargetDate, setModifyTargetDate] = useState<Date | undefined>(
    undefined
  );
  const [modifyIsEndOfEpisode, setModifyIsEndOfEpisode] = useState(false);
  const [modifyGoalType, setModifyGoalType] = useState<
    'short_term' | 'long_term'
  >('short_term');
  const [modifyInstruction, setModifyInstruction] = useState('');
  const [modifyReason, setModifyReason] = useState('');
  const [modifyIsSaving, setModifyIsSaving] = useState(false);
  const [isAddInterventionsOpen, setIsAddInterventionsOpen] = useState(false);

  useEffect(() => {
    if (isModifyFormOpen) {
      setModifyTargetDate(
        goal.target_date ? parseISOToLocalDate(goal.target_date) : undefined
      );
      setModifyIsEndOfEpisode(goal.is_for_end_of_episode);
      setModifyGoalType(goal.length ?? 'short_term');
      setModifyInstruction(goal.instruction ?? '');
      setModifyReason('');
    }
  }, [isModifyFormOpen, goal]);
  const [freeTextInterventionKeys, setFreeTextInterventionKeys] = useState<
    string[]
  >([]);
  const nextKeyRef = useRef(0);
  const [pendingTemplates, setPendingTemplates] = useState<
    InterventionTemplateDetail[]
  >([]);
  const [savedInterventions, setSavedInterventions] = useState<
    PatientInterventionDetail[]
  >(goal.interventions);
  const [trackProgressOpen, setTrackProgressOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [showGoalPerformanceBox, setShowGoalPerformanceBox] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    'met' | 'unattainable' | null
  >(null);
  const [showDeleteGoalConfirm, setShowDeleteGoalConfirm] = useState(false);
  const [pushDialogOpen, setPushDialogOpen] = useState(false);

  const hasProgressHistory =
    (goal.progress_history?.length ?? 0) > 0 ||
    savedInterventions.some(i => (i.progress_history?.length ?? 0) > 0);

  const typeLabel = goal.length === 'long_term' ? 'Long term' : 'Short term';
  const formattedDate = formatDateForDisplay(goal.target_date ?? '');

  const hasUnperformedInterventions = savedInterventions.some(
    intervention =>
      !(intervention.progress_history?.some(p => p.is_performed) ?? false)
  );

  const mostRecentProgress = getMostRecentProgressWithScore(
    goal.progress_history
  );
  const hasProgressForCurrentVisit =
    goal.progress_history?.some(p => p.task_id === visitId) ?? false;
  const lastAddressedValue = mostRecentProgress
    ? formatDateForDisplay(mostRecentProgress.task_date)
    : '---';
  const progressValue = mostRecentProgress?.progress_score ?? '---';

  const handleAddInterventions = (templates: InterventionTemplateDetail[]) => {
    setPendingTemplates(prev => {
      const existingIds = new Set(prev.map(t => t.id));
      return [...prev, ...templates.filter(t => !existingIds.has(t.id))];
    });
    setIsExpanded(true);
  };

  const handleInterventionDiscarded = (templateId: string) => {
    setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
  };

  const handleFreeTextInterventionSaved = (key: string) => {
    setFreeTextInterventionKeys(prev => prev.filter(k => k !== key));
    getInterventionsForPatientGoal({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(response => {
        setSavedInterventions(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load interventions');
      });
  };

  const handleInterventionSaved = (templateId: string) => {
    setPendingTemplates(prev => prev.filter(t => t.id !== templateId));
    getInterventionsForPatientGoal({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(response => {
        setSavedInterventions(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load interventions');
      });
  };

  const handleToggleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  const handleOpenAddInterventions = () => {
    setIsAddInterventionsOpen(true);
  };

  const handleCloseAddInterventions = () => {
    setIsAddInterventionsOpen(false);
  };

  const handleCancelFreeText = (key: string) => {
    setFreeTextInterventionKeys(prev => prev.filter(k => k !== key));
  };

  const handleUseFreeText = () => {
    const key = `free-text-${nextKeyRef.current++}`;
    setFreeTextInterventionKeys(prev => [...prev, key]);
    setIsExpanded(true);
  };

  const handleSaveForTemplate = (templateId: string) => () => {
    handleInterventionSaved(templateId);
  };

  const handleCancelForTemplate = (templateId: string) => () => {
    handleInterventionDiscarded(templateId);
  };

  const handleTrackPerformanceSelect = () => {
    setShowGoalPerformanceBox(true);
    setTrackProgressOpen(false);
  };

  const handleGoalPerformanceBoxClose = () => {
    setShowGoalPerformanceBox(false);
  };

  const handleGoalPerformanceSaved = () => {
    setShowGoalPerformanceBox(false);
    getPatientGoalsForVisit({
      path: { visit_id: visitId },
      // throwOnError: false would resolve with the AxiosError, silently
      // skipping the refresh — throw so the catch actually fires.
      throwOnError: true,
    })
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError('Failed to load goals');
      });
  };

  const handleMarkAsMet = () => {
    setTrackProgressOpen(false);
    setConfirmAction('met');
  };

  const handleMarkAsUnattainable = () => {
    setTrackProgressOpen(false);
    setConfirmAction('unattainable');
  };

  const handleConfirmAction = () => {
    markPatientGoalMet({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        // Surface the 4xx business message when there is one — e.g. a
        // concurrent push froze this goal between load and this action.
        console.error(error);
        showError(
          'Failed to mark goal as met',
          getBusinessErrorMessage(error) ??
            `Failed to mark goal "${goal.name}" as met.`
        );
      });
    setConfirmAction(null);
  };

  const handleConfirmUnattainable = (reason: string) => {
    markPatientGoalUnattainable({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      body: { unattainable_reason: reason },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError(
          'Failed to mark goal as unattainable',
          getBusinessErrorMessage(error) ??
            `Failed to mark goal "${goal.name}" as unattainable.`
        );
      });
    setConfirmAction(null);
  };

  const handleCancelConfirmAction = () => {
    setConfirmAction(null);
  };

  const handleModifyGoal = () => {
    setEditDropdownOpen(false);
    setIsModifyFormOpen(true);
  };

  const [isAccepting, setIsAccepting] = useState(false);

  const handleAcceptImport = () => {
    setIsAccepting(true);
    acceptImportedGoal({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(() => {
        showError('Failed to accept goal');
      })
      .finally(() => setIsAccepting(false));
  };

  const minDate = episodeStartDate
    ? parseISOToLocalDate(episodeStartDate)
    : undefined;
  const maxDate = episodeEndDate
    ? parseISOToLocalDate(episodeEndDate)
    : undefined;

  const handleModifyDateChange = (date: Date | undefined) => {
    setModifyIsEndOfEpisode(false);
    if (!date) {
      setModifyTargetDate(undefined);
      return;
    }
    if (minDate && maxDate) {
      const time = date.getTime();
      if (time < minDate.getTime()) {
        setModifyTargetDate(minDate);
      } else if (time > maxDate.getTime()) {
        setModifyTargetDate(maxDate);
      } else {
        setModifyTargetDate(date);
      }
    } else {
      setModifyTargetDate(date);
    }
  };

  const handleModifyEndOfEpisodeChange = (
    checked: boolean | 'indeterminate'
  ) => {
    const checkedVal = checked === true;
    setModifyIsEndOfEpisode(checkedVal);
    if (checkedVal && maxDate) {
      setModifyTargetDate(maxDate);
    }
  };

  const handleModifySave = () => {
    if (!modifyTargetDate || !modifyReason.trim()) return;
    setModifyIsSaving(true);
    updateGoalForVisit({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      body: {
        target_date: formatDateToISO(modifyTargetDate),
        is_for_end_of_episode: modifyIsEndOfEpisode,
        length: modifyGoalType,
        name: goal.name,
        instruction: modifyInstruction.trim(),
        reason: modifyReason.trim(),
      },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
        setIsModifyFormOpen(false);
      })
      .catch(() => {
        showError(
          'Failed to update goal',
          `Failed to update goal "${goal.name}".`
        );
      })
      .finally(() => setModifyIsSaving(false));
  };

  const handleModifyCancel = () => {
    setIsModifyFormOpen(false);
  };

  const handleRemoveGoalClick = () => {
    setEditDropdownOpen(false);
    setShowDeleteGoalConfirm(true);
  };

  const handleConfirmDeleteGoal = () => {
    setShowDeleteGoalConfirm(false);
    deleteGoalFromVisit({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(() => {
        showError(
          'Failed to delete goal',
          `Failed to delete goal "${goal.name}".`
        );
      });
  };

  const handlePushGoalClick = () => {
    setEditDropdownOpen(false);
    setPushDialogOpen(true);
  };

  const handleCancelPush = () => {
    setPushDialogOpen(false);
  };

  const handleGoalPushed = () => {
    setPushDialogOpen(false);
    getPatientGoalsForVisit({
      path: { visit_id: visitId },
      throwOnError: true,
    })
      .then(response => {
        if (response.data != null) {
          onGoalProgressSaved?.(response.data);
        }
      })
      .catch(error => {
        console.error(error);
        showError('Failed to load goals');
      });
  };

  const handlePerformanceSaved = () => {
    getInterventionsForPatientGoal({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      throwOnError: true,
    })
      .then(response => {
        setSavedInterventions(response.data ?? []);
      })
      .catch(() => {
        showError('Failed to load interventions');
      });
  };

  return (
    <>
      <Card className='self-start'>
        <CardContent className='p-0 space-y-1'>
          {isModifyFormOpen ? (
            <div className='p-4 space-y-4'>
              <div className='rounded-md border border-primary/20 bg-primary/5 px-3 py-2'>
                <p className='text-xs font-medium uppercase tracking-wide text-primary'>
                  Editing goal
                </p>
                <p className='mt-0.5 text-sm font-semibold text-foreground'>
                  {goal.name}
                </p>
              </div>
              <div className='space-y-2'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Target date for meeting a goal
                </p>
                <div className='flex flex-wrap items-center gap-4'>
                  <DatePicker
                    date={modifyTargetDate}
                    onDateChange={handleModifyDateChange}
                    datePickerSize='sm'
                    typable
                    showClearButton
                    minDate={minDate}
                    maxDate={maxDate}
                    className='w-44'
                  />
                  <div className='flex items-center gap-2'>
                    <Checkbox
                      id={`modify-end-of-episode-${goal.id}`}
                      checked={modifyIsEndOfEpisode}
                      onCheckedChange={handleModifyEndOfEpisodeChange}
                      checkboxSize='sm'
                    />
                    <Label
                      htmlFor={`modify-end-of-episode-${goal.id}`}
                      className='text-sm cursor-pointer select-none'
                    >
                      For end of episode
                    </Label>
                  </div>
                  <div className='flex shrink-0'>
                    <RadioButtonGroup
                      value={modifyGoalType}
                      onValueChange={val =>
                        setModifyGoalType(val as 'short_term' | 'long_term')
                      }
                      options={GOAL_TYPE_OPTIONS}
                      orientation='horizontal'
                    />
                  </div>
                </div>
              </div>

              <Separator />

              <div className='space-y-2'>
                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                  Instruction
                </p>
                <Textarea
                  placeholder='Enter goal instruction...'
                  value={modifyInstruction}
                  onChange={e => setModifyInstruction(e.target.value)}
                  textareaSize='default'
                  className='min-h-[80px]'
                />
              </div>

              <Separator />

              <div className='space-y-2'>
                <Label
                  htmlFor={`modify-reason-${goal.id}`}
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                >
                  Reasoning (Required)
                </Label>
                <Textarea
                  id={`modify-reason-${goal.id}`}
                  placeholder='Explain the reason for this modification...'
                  value={modifyReason}
                  onChange={e => setModifyReason(e.target.value)}
                  textareaSize='default'
                  className='min-h-[80px]'
                />
              </div>

              <div className='flex justify-end gap-2 pt-1'>
                <Button
                  size='sm'
                  variant='outline'
                  onClick={handleModifyCancel}
                  disabled={modifyIsSaving}
                >
                  Cancel
                </Button>
                <Button
                  size='sm'
                  disabled={
                    !modifyTargetDate || !modifyReason.trim() || modifyIsSaving
                  }
                  onClick={handleModifySave}
                >
                  {modifyIsSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className='space-y-3'>
                <div className='space-y-1'>
                  <div className='font-medium text-sm leading-tight'>
                    {goal.name}
                  </div>
                  <p className='text-sm text-muted-foreground leading-relaxed'>
                    {goal.instruction}
                  </p>
                </div>

                <div className='flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border/60 pt-2 text-xs text-muted-foreground'>
                  <span className='flex items-center gap-1'>
                    <Target className='h-3 w-3' />
                    {formattedDate}
                    {goal.is_for_end_of_episode && ' (end of episode)'}
                  </span>
                  <span className='flex items-center gap-1'>
                    <Tag className='h-3 w-3' />
                    {typeLabel}
                  </span>
                  {/* The chip shows while the goal still carries its source
                      values untouched (updated_at == imported_at — the same
                      test the overwrite guard uses). Accepting, or any edit,
                      clears it; imported_at stays on progress rows and the
                      print view as permanent provenance. */}
                  {goal.imported_unreviewed && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type='button'
                          aria-label='Imported goal — review options'
                        >
                          <Badge
                            variant='secondary'
                            className='cursor-pointer whitespace-nowrap'
                          >
                            Imported
                            {goal.backdated_creation_date
                              ? ` · initiated ${formatDateForDisplay(goal.backdated_creation_date)}`
                              : ''}
                          </Badge>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        align='start'
                        className='w-72 space-y-2 text-sm'
                      >
                        <p>
                          Imported from Kinnser. This goal carries its source
                          values until a clinician reviews it.
                        </p>
                        <ImportFindingsList
                          goal={goal}
                          interventions={savedInterventions}
                        />
                        <p className='text-xs text-muted-foreground'>
                          Accept keeps the goal and its interventions as
                          written. Editing the goal also accepts it.
                        </p>
                        {/* Accepting reviews the goal, not the visit, so it
                            stays available on a locked visit and on a met or
                            unattainable goal — the endpoint has no visit-status
                            gate either. Only editing needs an editable visit. */}
                        <div className='flex justify-end gap-2 pt-1'>
                          {!isViewOnly && !isComplete && (
                            <Button
                              size='sm'
                              variant='outline'
                              onClick={handleModifyGoal}
                            >
                              Edit goal
                            </Button>
                          )}
                          <Button
                            size='sm'
                            onClick={handleAcceptImport}
                            disabled={isAccepting}
                          >
                            {isAccepting ? 'Accepting…' : 'Accept into Roger'}
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                  <span>
                    last addressed:{' '}
                    <span className='font-semibold text-foreground'>
                      {lastAddressedValue}
                    </span>
                  </span>
                  <span>
                    progress:{' '}
                    <span className='font-semibold text-foreground'>
                      {progressValue}
                    </span>
                  </span>
                  {isExtended && (
                    <span className='whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800'>
                      Extended to next episode
                    </span>
                  )}
                </div>

                <div className='flex flex-wrap items-center gap-2'>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={handleToggleExpand}
                    className='inline-flex items-center gap-1.5'
                  >
                    {isExpanded ? 'Hide interventions' : 'Show interventions'}
                    <span className='text-muted-foreground'>
                      ({savedInterventions.length})
                    </span>
                    {isExpanded ? (
                      <ChevronUp className='h-3.5 w-3.5' />
                    ) : (
                      <ChevronDown className='h-3.5 w-3.5' />
                    )}
                  </Button>

                  {canShowTrackProgressMenu && (
                    <DropdownMenu
                      open={trackProgressOpen}
                      onOpenChange={setTrackProgressOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='sm'
                          variant='outline'
                          className='flex items-center gap-1'
                        >
                          Track Progress
                          <ChevronDown className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        {canTrackProgress && (
                          <DropdownMenuItem
                            onClick={handleTrackPerformanceSelect}
                          >
                            {hasProgressForCurrentVisit
                              ? 'Edit Performance'
                              : 'Track Progress'}
                          </DropdownMenuItem>
                        )}
                        {/* Marking met/unattainable is blocked for an
                            extended goal (EMR-1378 backend guard) -- it's
                            already been pushed forward, so hide these
                            actions rather than let the user hit a 400. */}
                        {!isExtended && canMarkGoalOutcome && (
                          <>
                            <DropdownMenuItem
                              disabled={hasUnperformedInterventions}
                              onClick={handleMarkAsMet}
                            >
                              Mark as met
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={hasUnperformedInterventions}
                              onClick={handleMarkAsUnattainable}
                            >
                              Mark as unattainable
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Editing and removing a goal are not progress actions. The
                      backend leaves goal update and delete ungated on purpose so
                      goals can be corrected wherever they were created, so these
                      stay available on notes that cannot record progress. An
                      order never gets this menu (canEditGoal/canDeleteGoal are
                      both false there). An extended goal is the exception: it's
                      frozen against edit/delete once pushed (EMR-1378), so hide
                      this menu. */}
                  {canShowEditMenu && (
                    <DropdownMenu
                      open={editDropdownOpen}
                      onOpenChange={setEditDropdownOpen}
                    >
                      <DropdownMenuTrigger asChild>
                        <Button
                          size='sm'
                          variant='outline'
                          className='flex items-center gap-1'
                        >
                          Edit
                          <ChevronDown className='h-3 w-3' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        {canDeleteGoal && (
                          <DropdownMenuItem
                            disabled={hasProgressHistory}
                            onClick={handleRemoveGoalClick}
                          >
                            Remove goal
                          </DropdownMenuItem>
                        )}
                        {canEditGoal && (
                          <DropdownMenuItem onClick={handleModifyGoal}>
                            Modify goal
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Push is its own capability (`canPushGoal`), independent of
                      the Edit menu above: an order can push a goal to the next
                      episode even though it can never edit/delete/mark it. */}
                  {canShowPushAction && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={handlePushGoalClick}
                      disabled={!nextEpisode}
                      title={
                        !nextEpisode
                          ? "The next episode hasn't been created yet"
                          : undefined
                      }
                      className='flex items-center gap-1'
                    >
                      Push to next episode
                    </Button>
                  )}
                </div>
              </div>

              {showGoalPerformanceBox && (
                <div className='pt-2 -mx-3 border-t border-border/60'>
                  <PerformanceBox
                    visitId={visitId}
                    patientGoalId={goal.id}
                    onCancel={handleGoalPerformanceBoxClose}
                    onSaveSuccess={handleGoalPerformanceSaved}
                  />
                </div>
              )}

              {isExpanded && (
                <div className='mt-1 space-y-2 rounded-md border border-border/60 bg-muted/20 p-3'>
                  {goal.progress_history &&
                    goal.progress_history.length > 0 && (
                      <div>
                        <h4 className='text-xs font-medium text-muted-foreground mb-1'>
                          Performance history
                        </h4>
                        <GoalPerformanceHistoryTable
                          progressHistory={goal.progress_history}
                        />
                      </div>
                    )}
                  {!isViewOnly &&
                    !isComplete &&
                    !isExtended &&
                    canAddInterventions && (
                      <div className='flex'>
                        <Button
                          size='sm'
                          onClick={handleOpenAddInterventions}
                          className='flex items-center gap-1'
                        >
                          <Plus className='h-3 w-3' />
                          Add Interventions
                        </Button>
                      </div>
                    )}
                  {(pendingTemplates.length > 0 ||
                    freeTextInterventionKeys.length > 0) && (
                    <div className='space-y-2 rounded-md border border-primary/20 bg-primary/5 p-2'>
                      <p className='text-xs font-medium text-primary'>
                        Interventions currently being added
                      </p>
                      <div className='grid grid-cols-1 items-start gap-2'>
                        {freeTextInterventionKeys.map(key => (
                          <CreateInterventionFromTemplateCard
                            key={key}
                            template={null}
                            onChange={() => {}}
                            showSaveButton
                            visitId={visitId}
                            patientGoalId={goal.id}
                            onSave={() => handleFreeTextInterventionSaved(key)}
                            onCancel={() => handleCancelFreeText(key)}
                          />
                        ))}
                        {pendingTemplates.map(template => (
                          <CreateInterventionFromTemplateCard
                            key={template.id}
                            template={template}
                            onChange={() => {}}
                            showSaveButton
                            visitId={visitId}
                            patientGoalId={goal.id}
                            onSave={handleSaveForTemplate(template.id)}
                            onCancel={handleCancelForTemplate(template.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {(pendingTemplates.length > 0 ||
                    freeTextInterventionKeys.length > 0) &&
                    savedInterventions.length > 0 && (
                      <div className='relative flex items-center py-1'>
                        <div className='flex-grow border-t border-border' />
                        <span className='mx-3 text-xs text-muted-foreground whitespace-nowrap'>
                          Existing interventions
                        </span>
                        <div className='flex-grow border-t border-border' />
                      </div>
                    )}
                  {savedInterventions.length === 0 &&
                  pendingTemplates.length === 0 &&
                  freeTextInterventionKeys.length === 0 ? (
                    <p className='text-xs text-muted-foreground italic'>
                      No interventions for this goal
                    </p>
                  ) : savedInterventions.length > 0 ? (
                    savedInterventions.map(intervention => (
                      <DisplayInterventionCard
                        key={intervention.id}
                        intervention={intervention}
                        visitId={visitId}
                        isViewOnly={isViewOnly}
                        canTrackProgress={canTrackProgress}
                        canPerformInterventions={canPerformInterventions}
                        canCompleteInterventions={canCompleteInterventions}
                        canDeleteIntervention={canDeleteInterventions}
                        isGoalExtended={isExtended}
                        onPerformanceSaved={handlePerformanceSaved}
                      />
                    ))
                  ) : null}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <AddInterventionsBox
        open={isAddInterventionsOpen}
        onClose={handleCloseAddInterventions}
        visitId={visitId}
        onAddInterventions={handleAddInterventions}
        onUseFreeText={handleUseFreeText}
      />
      <ConfirmationDialog
        open={confirmAction === 'met'}
        title='Mark goal as met?'
        message='This action cannot be undone and will mark all interventions for this patient as complete.'
        onAccept={handleConfirmAction}
        onCancel={handleCancelConfirmAction}
      />
      <UnattainableReasonDialog
        open={confirmAction === 'unattainable'}
        onAccept={handleConfirmUnattainable}
        onCancel={handleCancelConfirmAction}
      />
      <ConfirmationDialog
        open={showDeleteGoalConfirm}
        title='Delete goal?'
        message='You are about to permanently delete this goal. This action cannot be undone.'
        confirmLabel='Delete'
        onAccept={handleConfirmDeleteGoal}
        onCancel={() => setShowDeleteGoalConfirm(false)}
      />
      {nextEpisode && (
        <PushGoalDialog
          goal={goal}
          visitId={visitId}
          nextEpisode={nextEpisode}
          confirmOpen={pushDialogOpen}
          onCancel={handleCancelPush}
          onSuccess={handleGoalPushed}
        />
      )}
    </>
  );
};

export default DisplayGoalCard;
